import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { useActivityLog } from '@/composables/useActivityLog'
import { getMembershipConstraint } from '@/utils/tenantQuery'
import type { User, UserRole } from '@/types'
import type { UsersState } from './state'

export interface CreateUserData {
  email: string
  password: string
  displayName: string
  role: UserRole
  phone?: string
  organizationId?: string
  isOrganizationOwner?: boolean
  assignedOrganizationIds?: string[]
  autoGeneratePassword?: boolean
}

export interface UpdateUserData {
  displayName?: string
  role?: UserRole
  phone?: string
  organizationId?: string
  isOrganizationOwner?: boolean
  isActive?: boolean
  assignedOrganizationIds?: string[]
}

export const createActions = (state: UsersState) => {
  const { logCreate, logUpdate, logDelete, detectChanges } = useActivityLog()
  let globalUnsubscribe: Unsubscribe | null = null

  const mapUserDoc = (docSnap: { id: string; data: () => Record<string, unknown> }): User => {
    const data = docSnap.data()
    return {
      id: docSnap.id,
      tenantId: data.tenantId,
      email: data.email,
      displayName: data.displayName,
      role: data.role,
      tenantIds: data.tenantIds || undefined,
      tenantRoles: data.tenantRoles || undefined,
      organizationId: data.organizationId,
      isOrganizationOwner: data.isOrganizationOwner,
      assignedOrganizationIds: data.assignedOrganizationIds || [],
      avatarUrl: data.avatarUrl,
      phone: data.phone,
      language: data.language || 'es',
      theme: data.theme || 'light',
      telegramChatId: data.telegramChatId || undefined,
      telegramUserId: data.telegramUserId || undefined,
      telegramLinkedAt: (data.telegramLinkedAt as any)?.toDate?.() || undefined,
      telegramBotUsername: data.telegramBotUsername || undefined,
      isActive: data.isActive,
      createdAt: (data.createdAt as any)?.toDate?.() || new Date(),
      updatedAt: (data.updatedAt as any)?.toDate?.() || new Date(),
      createdBy: data.createdBy,
      updatedBy: data.updatedBy,
    } as User
  }

  const subscribeGlobal = (): void => {
    if (globalUnsubscribe) return
    state.isLoading = true
    const tenantConstraint = getMembershipConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []
    // Sin orderBy: array-contains + orderBy exige índice compuesto. Ordenamos en memoria.
    const q = query(collection(db, 'users'), ...baseConstraints)
    globalUnsubscribe = onSnapshot(q, (snapshot) => {
      state.users = snapshot.docs.map(mapUserDoc).sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0))
      state.isLoading = false
    }, (error) => {
      console.error('Users subscription error:', error)
      state.isLoading = false
    })
  }

  const unsubscribeGlobal = (): void => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
    }
  }

  const fetchUsers = async (): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const tenantConstraint = getMembershipConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []
      // Sin orderBy (array-contains + orderBy exige índice compuesto); ordenamos en memoria.
      const q = query(collection(db, 'users'), ...baseConstraints)
      const snapshot = await getDocs(q)

      state.users = snapshot.docs
        .map((doc) => mapUserDoc(doc))
        .sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0))
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al cargar usuarios'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const fetchUserById = async (userId: string): Promise<User | null> => {
    try {
      const docRef = doc(db, 'users', userId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        return null
      }

      const data = docSnap.data()
      return {
        id: docSnap.id,
        tenantId: data.tenantId,
        email: data.email,
        displayName: data.displayName,
        role: data.role,
        organizationId: data.organizationId,
        isOrganizationOwner: data.isOrganizationOwner,
        assignedOrganizationIds: data.assignedOrganizationIds || [],
        avatarUrl: data.avatarUrl,
        phone: data.phone,
        language: data.language || 'es',
        theme: data.theme || 'light',
        telegramChatId: data.telegramChatId || undefined,
        telegramUserId: data.telegramUserId || undefined,
        telegramLinkedAt: data.telegramLinkedAt?.toDate() || undefined,
        isActive: data.isActive,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        createdBy: data.createdBy,
        updatedBy: data.updatedBy,
      } as User
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al cargar usuario'
      throw error
    }
  }

  const createUser = async (data: CreateUserData): Promise<User> => {
    state.isLoading = true
    state.error = null

    try {
      const createUserFn = httpsCallable<Record<string, unknown>, {
        id: string
        tenantId: string | null
        email: string
        displayName: string
        role: string
        phone: string | null
        organizationId: string | null
        isOrganizationOwner: boolean
        assignedOrganizationIds: string[]
        language: string
        theme: string
        telegramChatId: string | null
        isActive: boolean
        createdAt: string
        updatedAt: string
        createdBy: string
        updatedBy: string
        added?: boolean
        emailSent?: boolean
      }>(functions, 'createUserFn')

      // Membership-aware (Modelo C): el usuario nuevo se crea/suma en el tenant ACTIVO
      // (impersonación o tenant seleccionado por el usuario multi-tenant), NO en el tenant
      // por defecto del caller. Espejo de deleteUser. Sin esto, la function cae al
      // callerData.tenantId (tenant home) e ignora el tenant al que te cambiaste.
      const authStore = useAuthStore()
      const result = await createUserFn({
        email: data.email,
        password: data.password,
        displayName: data.displayName,
        role: data.role,
        phone: data.phone,
        organizationId: data.organizationId,
        isOrganizationOwner: data.isOrganizationOwner,
        assignedOrganizationIds: data.assignedOrganizationIds,
        autoGeneratePassword: data.autoGeneratePassword,
        tenantId: authStore.effectiveTenantId || undefined,
      })

      const d = result.data
      if (d.emailSent === false) {
        window.alert(`El usuario se ${d.added ? 'agregó' : 'creó'} pero el email de credenciales a ${d.email} FALLÓ. Usá "Reenviar credenciales" desde el menú del usuario.`)
      }
      const createdUser: User = {
        id: d.id,
        tenantId: d.tenantId || undefined,
        email: d.email,
        displayName: d.displayName,
        role: d.role as User['role'],
        phone: d.phone || undefined,
        organizationId: d.organizationId || undefined,
        isOrganizationOwner: d.isOrganizationOwner,
        assignedOrganizationIds: d.assignedOrganizationIds,
        language: d.language as User['language'],
        theme: d.theme as User['theme'],
        telegramChatId: d.telegramChatId || undefined,
        isActive: d.isActive,
        createdAt: new Date(d.createdAt),
        updatedAt: new Date(d.updatedAt),
        createdBy: d.createdBy,
        updatedBy: d.updatedBy,
      }

      state.users.unshift(createdUser)
      await logCreate('user', createdUser.id, createdUser.displayName)

      return createdUser
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al crear usuario'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const updateUser = async (userId: string, data: UpdateUserData): Promise<User> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.userId || 'system'

      // Get current user data for change detection
      const currentUser = state.users.find((u) => u.id === userId)
      if (!currentUser) {
        throw new Error('Usuario no encontrado')
      }

      // Strip undefined values — Firestore rejects them
      const updateData: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      }
      for (const [key, value] of Object.entries(data)) {
        if (value !== undefined) {
          updateData[key] = value
        }
      }

      console.log('[UsersStore] updateUser data received:', JSON.stringify(data))
      console.log('[UsersStore] updateData to Firestore:', Object.keys(updateData), 'assignedOrganizationIds:', updateData.assignedOrganizationIds)

      await updateDoc(doc(db, 'users', userId), updateData)
      console.log('[UsersStore] updateDoc success for user:', userId)

      // Detect changes for activity log
      const changes = detectChanges(
        currentUser as unknown as Record<string, unknown>,
        data as Partial<Record<string, unknown>>,
        ['displayName', 'role', 'phone', 'organizationId', 'isActive', 'assignedOrganizationIds']
      )

      // Strip undefined from data so we don't overwrite existing values in local state
      const cleanData: Partial<User> = {}
      for (const [key, value] of Object.entries(data)) {
        if (value !== undefined) {
          (cleanData as Record<string, unknown>)[key] = value
        }
      }

      const updatedUser: User = {
        ...currentUser,
        ...cleanData,
        updatedAt: new Date(),
        updatedBy: currentUserId,
      }

      // Update in local state
      const index = state.users.findIndex((u) => u.id === userId)
      if (index !== -1) {
        state.users[index] = updatedUser
      }

      if (changes.length > 0) {
        await logUpdate('user', userId, changes, updatedUser.displayName)
      }

      return updatedUser
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al actualizar usuario'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const deleteUser = async (userId: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const user = state.users.find((u) => u.id === userId)

      // Membership-aware (Modelo C): quita al usuario del tenant ACTIVO (tenantIds+rol); si era su
      // último tenant, la function lo borra completo. Vía Cloud Function (Admin SDK, autorizado).
      const authStore = useAuthStore()
      const deleteUserFn = httpsCallable(functions, 'deleteUserFn')
      await deleteUserFn({ userId, tenantId: authStore.effectiveTenantId || undefined })

      state.users = state.users.filter((u) => u.id !== userId)

      if (user) {
        await logDelete('user', userId, user.displayName)
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al eliminar usuario'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const toggleUserActive = async (userId: string): Promise<void> => {
    const user = state.users.find((u) => u.id === userId)
    if (!user) return

    await updateUser(userId, { isActive: !user.isActive })
  }

  // Regenera una clave temporal, la actualiza en Auth, fuerza el cambio y la reenvía por email.
  // Sirve cuando el email de bienvenida falló (la clave no se guarda) o para reenviar credenciales.
  const resendCredentials = async (userId: string): Promise<{ emailSent: boolean }> => {
    const fn = httpsCallable<{ userId: string }, { success: boolean; emailSent: boolean }>(
      functions, 'resendCredentialsFn',
    )
    const res = await fn({ userId })
    return { emailSent: res.data.emailSent }
  }

  const setSelectedUser = (user: User | null): void => {
    state.selectedUser = user
  }

  const setFilters = (filters: Partial<UsersState['filters']>): void => {
    state.filters = { ...state.filters, ...filters }
  }

  const assignOperatorToOrganization = async (operatorId: string, orgId: string): Promise<void> => {
    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.userId || 'system'

      await updateDoc(doc(db, 'users', operatorId), {
        assignedOrganizationIds: arrayUnion(orgId),
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      })

      // Update local state
      const user = state.users.find((u) => u.id === operatorId)
      if (user) {
        if (!user.assignedOrganizationIds) user.assignedOrganizationIds = []
        if (!user.assignedOrganizationIds.includes(orgId)) {
          user.assignedOrganizationIds = [...user.assignedOrganizationIds, orgId]
        }
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al asignar operador'
      throw error
    }
  }

  const unassignOperatorFromOrganization = async (operatorId: string, orgId: string): Promise<void> => {
    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.userId || 'system'

      await updateDoc(doc(db, 'users', operatorId), {
        assignedOrganizationIds: arrayRemove(orgId),
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      })

      // Update local state
      const user = state.users.find((u) => u.id === operatorId)
      if (user && user.assignedOrganizationIds) {
        user.assignedOrganizationIds = user.assignedOrganizationIds.filter((id) => id !== orgId)
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al desasignar operador'
      throw error
    }
  }

  const clearError = (): void => {
    state.error = null
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    fetchUsers,
    fetchUserById,
    createUser,
    updateUser,
    deleteUser,
    resendCredentials,
    toggleUserActive,
    setSelectedUser,
    setFilters,
    clearError,
    assignOperatorToOrganization,
    unassignOperatorFromOrganization,
  }
}
